/**
 * @author        esraa.afifi@zumper.com (Esraa Afifi)
 * @copyright     Copyright (c) 2021, Zumper
 * @description   Desktop header
 */

import { chakra, useColorModeValue } from '@chakra-ui/react'
import { Global } from '@emotion/react'
import { ZumperFullDark } from '@zcode-ui/icons'
import { escHandler } from '@zcode/a11y-utils'
import { keepBetaParam, locationDescriptorToString } from '@zcode/nr-utils'
import { useAnalyticsTrack } from '@zcode/nr/es/shared/packages/analytics-track'
import React, { Fragment, useEffect } from 'react'
import { useHistory } from 'react-router-dom'
import { getOrigin } from '../../utils/getOrigin'
import { UserMenu, type UserMenuProps } from '../UserMenu'
import * as styles from './style'

export type HomepageHeaderDesktopProps = {
  hasMaxWidth?: boolean
  isFavoritesView?: boolean
  isFixed?: boolean
  isMessagedView?: boolean
  isSavedSearchesView?: boolean
  transparent?: boolean
} & UserMenuProps

export const HomepageHeaderDesktop = (props: HomepageHeaderDesktopProps) => {
  const {
    hasMaxWidth,
    isFavoritesView,
    isFixed,
    isHomepageView,
    isMessagedView,
    isSavedSearchesView,
    onEscPressed,
    transparent,
  } = props

  const origin = getOrigin(isFavoritesView, isHomepageView, isMessagedView, isSavedSearchesView)

  const onLogoClick = useAnalyticsTrack('Navbar', 'Logo', origin)
  const bgColor = useColorModeValue('#f5f1e9', 'blue.900')

  useEffect(() => {
    onEscPressed && window.document.addEventListener('keydown', escHandler(onEscPressed))
    return () => {
      onEscPressed && window.document.removeEventListener('keydown', escHandler(onEscPressed))
    }
  }, [onEscPressed])

  return (
    <Fragment>
      <Global styles={styles.root} />
      <chakra.nav
        bgColor={transparent ? 'transparent' : bgColor}
        css={[
          styles.navbar,
          isFixed && styles.fixedNavbar,
          transparent && styles.transparentThemeNavbar,
        ]}
      >
        <div css={[styles.navbarContent, hasMaxWidth && styles.hasMaxWidth]}>
          <div css={styles.homeButton}>
            <a
              aria-label="Zumper"
              href={locationDescriptorToString(keepBetaParam('/'), useHistory())}
              onClick={onLogoClick}
            >
              <ZumperFullDark size={42} />
            </a>
          </div>

          <UserMenu {...props} />
        </div>
      </chakra.nav>
    </Fragment>
  )
}
